import type { ShowLockedPeriods } from "@components/accounting/schedules/useScheduleLockedPeriodsDialog";
import React, { useMemo } from "react";
import { Invoice } from "@common/types/invoice";
import { ScheduleSourceType } from "@common/types/domains/accounting/schedule";
import { ScheduleLifecycleRequest } from "@api/scheduleTemplate.api";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { filterTemplatesForDocument } from "@components/accounting/schedules/documentScheduleTemplates";
import { SchedulePicker } from "@components/accounting/schedules/SchedulePicker";
import { usePanelScheduleTemplates } from "@components/accounting/schedules/usePanelScheduleTemplates";
import {
    isScheduleCellInteractive,
    toScheduleCardValues,
} from "@components/invoices/views/scheduleCellState";
import { ScheduleCellContent } from "@components/invoices/views/ScheduleCellContent";
import { ScheduleConfirmRequest } from "./useInvoiceScheduleApply";
import { useInvoiceSchedulePicker } from "./useInvoiceSchedulePicker";
import accountFieldStyles from "@components/transactions/TransactionDetails/TransactionDetailsAccount.module.scss";

export interface InvoiceDetailsScheduleCallbacks {
    onCreateSchedule?: (options: { entityId: number }) => void;
    onScheduleApplied?: () => Promise<void> | void;
    onRequestScheduleConfirm?: (
        options: ScheduleConfirmRequest,
    ) => Promise<boolean>;
    onRequestLifecycleAction?: (request: ScheduleLifecycleRequest) => void;
    /** Surfaces inside an overlay pass a local dialog; the default opens the global one. */
    showLockedPeriods?: ShowLockedPeriods;
}

interface Props extends InvoiceDetailsScheduleCallbacks {
    invoice: Invoice;
}

export const InvoiceDetailsSchedule: React.FC<Props> = ({
    invoice,
    ...callbacks
}) => {
    const {
        schedules: isSchedulesEnabled,
        schedulesExpenses: isExpensesEnabled,
    } = useTypedFlags();
    const entityTemplates = usePanelScheduleTemplates(invoice.entityId);
    const scheduleTemplates = useMemo(
        () =>
            filterTemplatesForDocument({
                templates: entityTemplates,
                documentType: ScheduleSourceType.invoice,
                isExpensesEnabled,
            }),
        [entityTemplates, isExpensesEnabled],
    );
    const { state, pickerProps, overlays } = useInvoiceSchedulePicker({
        invoice,
        callbacks,
    });

    if (!isSchedulesEnabled) {
        return null;
    }

    if (!isScheduleCellInteractive(state)) {
        return <ScheduleCellContent state={state} />;
    }

    return (
        <>
            <SchedulePicker
                compact
                compactTriggerClassName={accountFieldStyles.ledgerTrigger}
                templates={scheduleTemplates}
                applied={toScheduleCardValues(state)}
                {...pickerProps}
            />
            {overlays}
        </>
    );
};
